import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Translate } from 'react-jhipster';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PlaceIcon from '@mui/icons-material/Place';
import ImageIcon from '@mui/icons-material/Image';
import ArticleIcon from '@mui/icons-material/Article';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import LanguageIcon from '@mui/icons-material/Language';
import CategoryIcon from '@mui/icons-material/Category';
import WebAssetIcon from '@mui/icons-material/WebAsset';
import RecentActorsIcon from '@mui/icons-material/RecentActors';

import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getEntities as getTours } from 'app/entities/tour/tour.reducer';
import { getEntities as getPlaces } from 'app/entities/place/place.reducer';
import { getEntities as getImageFiles } from 'app/entities/image-file/image-file.reducer';
import { getEntities as getContents } from 'app/entities/content/content.reducer';
import { getEntities as getPrompts } from 'app/entities/prompt/prompt.reducer';
import { getEntities as getLanguages } from 'app/entities/language/language.reducer';
import { getEntities as getTourCategories } from 'app/entities/tour-category/tour-category.reducer';
import { getEntities as getPlaceCategories } from 'app/entities/place-category/place-category.reducer';
import { getEntities as getWebPages } from 'app/entities/web-page/web-page.reducer';

interface StatCardProps {
  title: React.ReactNode;
  count: number;
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ title, count, icon, color, onClick }) => (
  <Card
    sx={{
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': onClick ? { transform: 'translateY(-4px)', boxShadow: 4 } : {},
    }}
    onClick={onClick}
  >
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Avatar
        sx={{
          bgcolor: `${color}20`,
          color,
          width: 56,
          height: 56,
        }}
      >
        {icon}
      </Avatar>
      <Box>
        <Typography variant="h4" component="div" fontWeight="bold">
          {count}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const account = useAppSelector(state => state.authentication.account);

  const tours = useAppSelector(state => state.tour.entities);
  const places = useAppSelector(state => state.place.entities);
  const imageFiles = useAppSelector(state => state.imageFile.entities);
  const contents = useAppSelector(state => state.content.entities);
  const prompts = useAppSelector(state => state.prompt.entities);
  const languages = useAppSelector(state => state.language.entities);
  const tourCategories = useAppSelector(state => state.tourCategory.entities);
  const placeCategories = useAppSelector(state => state.placeCategory.entities);
  const webPages = useAppSelector(state => state.webPage.entities);

  const tourLoading = useAppSelector(state => state.tour.loading);
  const placeLoading = useAppSelector(state => state.place.loading);
  const imageLoading = useAppSelector(state => state.imageFile.loading);
  const contentLoading = useAppSelector(state => state.content.loading);
  const promptLoading = useAppSelector(state => state.prompt.loading);
  const languageLoading = useAppSelector(state => state.language.loading);
  const tourCategoryLoading = useAppSelector(state => state.tourCategory.loading);
  const placeCategoryLoading = useAppSelector(state => state.placeCategory.loading);
  const webPageLoading = useAppSelector(state => state.webPage.loading);

  const loading =
    tourLoading ||
    placeLoading ||
    imageLoading ||
    contentLoading ||
    promptLoading ||
    languageLoading ||
    tourCategoryLoading ||
    placeCategoryLoading ||
    webPageLoading;

  useEffect(() => {
    dispatch(getTours({ size: 1000, sort: 'createdDate,desc' }));
    dispatch(getPlaces({ size: 1000 }));
    dispatch(getImageFiles({ size: 1000 }));
    dispatch(getContents({ size: 1000 }));
    dispatch(getPrompts({ size: 1000 }));
    dispatch(getLanguages({ size: 1000 }));
    dispatch(getTourCategories({ size: 1000 }));
    dispatch(getPlaceCategories({ size: 1000 }));
    dispatch(getWebPages({ size: 1000 }));
  }, [dispatch]);

  const sortedTours = [...tours].sort((a, b) => {
    const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
    const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
    return dateB - dateA;
  });

  const last3Tours = sortedTours.slice(0, 3);

  const languageChips = languages.map(lang => (
    <Chip
      key={lang.id}
      icon={lang.icon ? <span style={{ fontSize: '1.2em', marginLeft: '8px' }}>{lang.icon}</span> : <LanguageIcon />}
      label={lang.title || lang.code}
      variant="outlined"
      sx={{ m: 0.5 }}
    />
  ));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <Translate contentKey="dashboard.title">Dashboard</Translate>
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <Translate contentKey="dashboard.welcome" interpolate={{ name: account?.firstName || account?.login }} />
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard title={<Translate contentKey="dashboard.tours">Tours</Translate>} count={tours.length} icon={<CalendarTodayIcon />} color="#1976d2" onClick={() => navigate('/tour')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard title={<Translate contentKey="dashboard.places">Places</Translate>} count={places.length} icon={<PlaceIcon />} color="#2e7d32" onClick={() => navigate('/place')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title={<Translate contentKey="dashboard.images">Images</Translate>}
                count={imageFiles.length}
                icon={<ImageIcon />}
                color="#ed6c02"
                onClick={() => navigate('/image-file')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title={<Translate contentKey="dashboard.content">Content</Translate>}
                count={contents.length}
                icon={<ArticleIcon />}
                color="#9c27b0"
                onClick={() => navigate('/content')}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard title={<Translate contentKey="dashboard.prompts">Prompts</Translate>} count={prompts.length} icon={<FlashOnIcon />} color="#00bcd4" onClick={() => navigate('/prompt')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title={<Translate contentKey="dashboard.tourCategories">Tour Categories</Translate>}
                count={tourCategories.length}
                icon={<CategoryIcon />}
                color="#f44336"
                onClick={() => navigate('/tour-category')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title={<Translate contentKey="dashboard.placeCategories">Place Categories</Translate>}
                count={placeCategories.length}
                icon={<CategoryIcon />}
                color="#795548"
                onClick={() => navigate('/place-category')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title={<Translate contentKey="dashboard.webPages">Web Pages</Translate>}
                count={webPages.length}
                icon={<WebAssetIcon />}
                color="#607d8b"
                onClick={() => navigate('/web-page')}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LanguageIcon color="primary" />
                  <Translate contentKey="dashboard.languages">Languages</Translate>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {languageChips.length > 0 ? (
                    languageChips
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      <Translate contentKey="dashboard.noLanguagesFound">No languages found</Translate>
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RecentActorsIcon color="primary" />
                  <Translate contentKey="dashboard.lastTours">Last 3 Tours Created</Translate>
                </Typography>
                <List>
                  {last3Tours.length > 0 ? (
                    last3Tours.map(tour => (
                      <ListItem
                        key={tour.id}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        onClick={() => navigate(`/tour/${tour.id}`)}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <CalendarTodayIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={tour.code || tour.id}
                          secondary={tour.createdDate ? new Date(tour.createdDate as any).toLocaleDateString() : 'No date'}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color="text.secondary">
                            <Translate contentKey="dashboard.noToursFound">No tours found</Translate>
                          </Typography>
                        }
                      />
                    </ListItem>
                  )}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
